import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { TundishShroud } from "../model/TundishShroud";
import { getTundishShrouds } from "../repositories/TundishShroudRepository";

type Props = {
  value: number | null;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  highlight?: boolean;
  disableClearable?: boolean;
};

export const TundishShroudSelector = ({
  value,
  onChange,
  onBlur = () => {},
  onFocus = () => {},
  disabled = false,
  onOpen = () => {},
  onClose = () => {},
  highlight = false,
  disableClearable = false,
}: Props) => {
  const [tundishShrouds, setTundishShrouds] = useState<TundishShroud[]>([]);
  const [selectedTundishShroud, setSelectedTundishShroud] =
    useState<TundishShroud | null>(null);
  const filteredTundishShroudsRef = useRef<TundishShroud[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const loadData = async () => {
    try {
      setIsLoading(true);
      const resp = await getTundishShrouds();
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as TundishShroud[];
        setTundishShrouds(rows);
      } else {
        setError(resp.message ?? "Error loading tundish shrouds");
      }
    } catch (error) {
      setError("Error loading tundish shrouds");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (tundishShrouds.length > 0 && value) {
      const tundishShroud = tundishShrouds.find(
        (ts) => ts.TundishShroudId === value,
      );
      setSelectedTundishShroud(tundishShroud || null);
    } else {
      setSelectedTundishShroud(null);
    }
  }, [value, tundishShrouds]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (filteredTundishShroudsRef.current.length > 0) {
        const firstTundishShroud = filteredTundishShroudsRef.current[0];
        setSelectedTundishShroud(firstTundishShroud);
        if (firstTundishShroud?.TundishShroudId) {
          onChange(firstTundishShroud.TundishShroudId);
        }
        inputRef.current?.blur();
      }
    }
  };

  return (
    <>
      <Autocomplete
        id="tundish-shroud-selector"
        disableClearable={disableClearable}
        clearOnEscape={!disableClearable}
        options={tundishShrouds || []}
        getOptionLabel={(option) => option.TundishShroudName || ""}
        value={selectedTundishShroud}
        onChange={(event, newValue) => {
          setSelectedTundishShroud(newValue);
          if (newValue?.TundishShroudId) {
            onChange(newValue.TundishShroudId);
          }
        }}
        onOpen={onOpen}
        onClose={onClose}
        onInputChange={(event, value, reason) => {
          if (reason === "input") {
            filteredTundishShroudsRef.current = tundishShrouds.filter(
              (tundishShroud) =>
                tundishShroud.TundishShroudName?.toLowerCase().includes(
                  value.toLowerCase(),
                ),
            );
          }
        }}
        isOptionEqualToValue={(option, value) =>
          option.TundishShroudId === value.TundishShroudId
        }
        disabled={disabled || isLoading}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label="Tundish Shroud"
            variant="outlined"
            fullWidth
            sx={
              highlight
                ? {
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(249, 248, 113, 0.35)",
                    },
                  }
                : undefined
            }
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            onFocus={onFocus}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
              inputLabel: {
                sx: {
                  fontSize: "clamp(.7rem,.8rem,1rem)",
                },
              },
            }}
          />
        )}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
